import AsyncStatus from 'mini-common-schema/lib/schema/AsyncStatus';
import isItemView from 'mini-mail-list-store/lib/utils/isItemView';
import SearchTableQuery from 'mini-mail-list-store/lib/store/schema/SearchTableQuery';
import { createSubstrateEntityRequest } from 'mini-3s-service/lib/utils/createSubstrateEntityRequest';
import { createSubstrateSearchRequest } from 'mini-3s-service/lib/utils/createSubstrateSearchRequest';
import { execute3SQueryService } from 'mini-3s-service/lib/services/execute3SQueryService';
import { getMailSearchResultsFromSubstrateSearchResponse } from './getMailSearchResultsFromResponse';
import { getSearchResultsLogicalId } from 'mini-3s-search-store/lib/selectors/getSearchResultsLogicalId';
import { MAIL_QUERY_PAGE_SIZE } from 'mini-3s-service/lib/utils/constants';
import { MailSearchResults } from '../store/schema/MailSearchResults';
import { SubstrateEntityType } from 'mini-3s-service/lib/schema/SubstrateEntityType';
import {
    doesSubstrateResponseHasError,
    doesSubstrateResponseHasNetworkError,
} from 'mini-3s-service/lib/utils/doesSubstrateResponseHasError';

/**
 * Returns a promise which resolves when 3S query service call completes.
 * An object containing mail results are returned when the promise resolves.
 * @param searchTableQuery
 */
export const get3sSearchResults = async (
    searchTableQuery: SearchTableQuery
): Promise<MailSearchResults> => {
    /** create an EntityRequest object for Mail query request */
    const substrateEntityItemType = isItemView()
        ? SubstrateEntityType.Message
        : SubstrateEntityType.Conversation;

    const { displayQueryString, queryString, rowOffset, searchFolderScopeId, searchSessionGuid } =
        searchTableQuery ?? {};

    const logicalId = getSearchResultsLogicalId();

    const entityRequest = createSubstrateEntityRequest(
        queryString,
        substrateEntityItemType,
        rowOffset,
        MAIL_QUERY_PAGE_SIZE,
        searchFolderScopeId,
        displayQueryString
    );

    /** create searchRequest from entityRequest */
    const searchRequest = createSubstrateSearchRequest(searchSessionGuid, logicalId, [
        entityRequest,
    ]);

    /** get service call result */
    const searchResponse = await execute3SQueryService(searchRequest, searchSessionGuid);

    /** If there is an error, handle it here */
    if (doesSubstrateResponseHasError(searchResponse)) {
        return {
            areMoreResultsAvailable: false,
            rows: [],
            asyncStatus: doesSubstrateResponseHasNetworkError(searchResponse)
                ? AsyncStatus.Error
                : AsyncStatus.ServerError,
        };
    }

    /** If there is no error, get the results from response */
    return getMailSearchResultsFromSubstrateSearchResponse(
        searchResponse,
        searchRequest.TimeZone,
        substrateEntityItemType
    );
};
